import React, { useState, DragEvent } from "react";
import { UseFormReturn, Controller } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils"; // Adjust path
import { FileText, Upload, X } from "lucide-react";

interface FileUploadProps {
  name: string;
  label?: string;
  form: UseFormReturn<any>;
  accept?: string;
  prompt?: string;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  name,
  label,
  form,
  accept = "image/*",
  prompt = "Drag & drop an image or click to select",
}) => {
  const [preview, setPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleDrop = (e: DragEvent, onChange: any) => {
    e.preventDefault();
    const files = e.dataTransfer.files;

    if (files.length) {
      onChange(files[0]);
      setSelectedFile(files[0]);
      setPreview(URL.createObjectURL(files[0]));
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, onChange: any) => {
    const file = e.target.files?.[0];
    if (file) {
      onChange(file);
      setSelectedFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };
  const handleRemove = (onChange: any) => {
    onChange(null);
    setPreview(null);
    setSelectedFile(null);
  };

  const isImagePreview = selectedFile?.type.startsWith("image/");

  return (
    <Controller
      name={name}
      control={form.control}
      render={({ field, fieldState }) => (
        <div className="space-y-2">
          {label && <label className="text-sm font-medium">{label}</label>}

          <div
            onDrop={(e) => handleDrop(e, field.onChange)}
            onDragOver={(e) => e.preventDefault()}
            className={cn(
              "border-2 border-dashed rounded-lg p-4 flex flex-col items-center justify-center cursor-pointer",
              fieldState.error && "border-destructive"
            )}
            onClick={() => {
              const input = document.getElementById(`${name}-input`) as HTMLInputElement;
              if (input) input.click();
            }}
          >
            {preview && isImagePreview ? (
              <div className="relative">
                <img src={preview} alt="preview" className="w-32 h-32 object-cover rounded" />
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-1 right-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(field.onChange);
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : selectedFile ? (
              <div className="relative flex min-w-0 max-w-full flex-col items-center gap-2 rounded border bg-muted/40 p-4 pr-10">
                <FileText className="h-8 w-8 text-muted-foreground" />
                <span className="max-w-64 truncate text-sm font-medium">{selectedFile.name}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(field.onChange);
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="text-center flex flex-col items-center space-y-2">
                <Upload className="h-6 w-6 text-gray-500" />
                <span className="text-sm text-gray-600">{prompt}</span>
              </div>
            )}
            <input
              id={`${name}-input`}
              accept={accept}
              type="file"
              className="hidden"
              onChange={(e) => handleChange(e, field.onChange)}
            />
          </div>

          {fieldState.error && <p className="text-sm text-destructive">{fieldState.error.message}</p>}
        </div>
      )}
    />
  );
};
